import { useState } from "react";
import useAxiosInstance from "../../hooks/useAxiosInstance";
import { API_RESULT, API_VERSION } from "../../enums/common.enum";
import {
  errorToast,
  handleError,
  successToast,
  transformAllSupplierPurchases,
} from "../../utils/common";
import { Box, CircularProgress, Grid, Stack, TextField } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useDispatch, useSelector } from "react-redux";
import { transferPurchaseUPISchema } from "../../schemas/payment";
import { Form, Formik } from "formik";
import { setAllSupplierPurchase } from "../../store/actions/useActions";

export const QrUpiIdAndNameView = ({
  setFieldValue,
  errors,
  touched,
  handleBlur,
  values,
  qrData,
  setQrData,
}: any) => {
  const [verifyLoading, setVerifyLoading] = useState(false);
  const privateInstance = useAxiosInstance();

  const findUpiByUpiId = async (val: string, setFieldValue: any) => {
    setVerifyLoading(true);
    try {
      const result = await privateInstance.get(
        `${API_VERSION.V1}/orders/payments/verify-upi?upi_id=${val}`
      );
      if (result?.data?.status === API_RESULT.SUCCESS) {
        setQrData(result.data.response);
        setFieldValue("upi_name", result.data.response.account_holder_name);
      } else {
        errorToast(result.data.message);
        setQrData({
          ...qrData,
          account_holder_name: "",
        });
        setFieldValue("upi_name", "");
      }
    } catch (err) {
      handleError(err);
    } finally {
      setVerifyLoading(false);
    }
  };

  return (
    <>
      <Grid container spacing={2} sx={{ mt: 3 }}>
        <Grid item xs={12} md={5}>
          <TextField
            fullWidth
            id="upi_id"
            label="UPI ID"
            size="small"
            value={values?.upi_id || ""}
            disabled
            onChange={(e) => {
              setFieldValue("upi_id", e.target.value);
              setFieldValue("upi_name", "");
            }}
            onBlur={handleBlur}
            helperText={errors.upi_id && touched.upi_id ? errors.upi_id : ""}
            error={errors.upi_id && touched.upi_id ? true : false}
            InputLabelProps={{ required: true }}
          />
        </Grid>

        <Grid item xs={12} md={2}>
          <LoadingButton
            fullWidth
            size="medium"
            color="primary"
            loading={verifyLoading}
            loadingIndicator={
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            }
            variant="contained"
            type="button"
            onClick={() => findUpiByUpiId(values?.upi_id, setFieldValue)}
            disabled={!values?.upi_id}
            sx={{
              height: "40px",
            }}
          >
            Verify
          </LoadingButton>
        </Grid>

        <Grid item xs={12} md={5}>
          <TextField
            fullWidth
            id="upi_name"
            label="UPI NAME"
            size="small"
            value={values?.upi_name}
            disabled
            InputLabelProps={{ required: true }}
            helperText={
              errors.upi_name && touched.upi_name ? errors.upi_name : ""
            }
            error={errors.upi_name && touched.upi_name ? true : false}
          />
        </Grid>
      </Grid>
    </>
  );
};

const UpiPayment = ({ purchaseData, lat, long, setPaymentModal }: any) => {
  const [qrData, setQrData] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const privateInstance = useAxiosInstance();
  const dispatch = useDispatch();
  let invoiceToManage = useSelector(
    (state: any) => state.supplier.allPurchaseData
  );

  const handleSubmit = async (values: any, actions: any) => {
    let payload = {
      order_id: purchaseData?.purchase_id,
      payment_partner: "supplier",
      payment_type: "UPI",
      upi_id: values?.upi_id,
      upi_name: values?.upi_name,
      provider_id: purchaseData?.location_id,
      location_id: purchaseData?.location_id,
      amount: values.payable_amount.toString(),
      discount_pct: "0.0",
      location: `${lat},${long}`,
      notes: "",
      other_charges: "0",
    };

    try {
      const result = await privateInstance.post(
        `${API_VERSION.V1}/orders/payments/transfer`,
        payload
      );
      if (result?.data?.status === API_RESULT.SUCCESS) {
        successToast("Payment initiated");
        setPaymentModal(false);
        //update
        dispatch(
          setAllSupplierPurchase(
            transformAllSupplierPurchases(
              invoiceToManage,
              purchaseData.purchase_id,
              result.data.response
            )
          )
        );
      } else {
        errorToast(result.data.message);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Formik
        initialValues={{
          upi_id: purchaseData?.beneficiary_details.upi_id || "",
          upi_name: purchaseData?.beneficiary_details.upi_name || "",
          invoice_amount: purchaseData?.invoice_amount || 0,
          payable_amount: purchaseData?.payable_amount || 0,
        }}
        validationSchema={transferPurchaseUPISchema}
        onSubmit={(values: any, actions: any) => {
          setLoading(true);
          actions.setSubmitting(false);
          handleSubmit(values, actions);
        }}
      >
        {(props: any) => {
          const {
            values,
            touched,
            errors,
            handleBlur,
            isSubmitting,
            setFieldValue,
          } = props;
          return (
            <Form>
              <Stack direction="row">
                <QrUpiIdAndNameView
                  setFieldValue={setFieldValue}
                  errors={errors}
                  touched={touched}
                  handleBlur={handleBlur}
                  values={values}
                  qrData={qrData}
                  setQrData={setQrData}
                />
              </Stack>
              <Grid container spacing={2} sx={{ mt: 2.5 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    id="invoice_amount"
                    label="INVOICE AMOUNT"
                    size="small"
                    value={values.invoice_amount}
                    disabled
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    id="payable_amount"
                    label="PAYABLE AMOUNT"
                    size="small"
                    value={values.payable_amount}
                    disabled
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 5, textAlign: "center" }}>
                <LoadingButton
                  type="submit"
                  color="primary"
                  variant="contained"
                  loading={loading}
                  loadingIndicator={
                    <CircularProgress size={20} sx={{ color: "#fff" }} />
                  }
                  disabled={isSubmitting}
                >
                  Confirm payment
                </LoadingButton>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default UpiPayment;
